import { useTranslation } from 'react-i18next';
import { LayoutGrid } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
} from '@/shared/components/ui/dropdown-menu';
import type { GroupByOption } from '../../types';

const GROUP_BY_OPTIONS: GroupByOption[] = ['group', 'status', 'assignee', 'priority', 'none'];

interface GroupByMenuProps {
  value: GroupByOption;
  onChange: (value: GroupByOption) => void;
  /** Full data-testid for the trigger button — the two consumers (List, Timeline) never render at once, so option testids stay shared. */
  triggerTestId: string;
}

/**
 * "Agrupar por" dropdown — shared between the List view's `ActionToolbar` and the
 * Timeline's `TimelineToolbar`. Same full option set in both places; the component
 * doesn't specialize per consumer.
 */
export function GroupByMenu({ value, onChange, triggerTestId }: GroupByMenuProps) {
  const { t } = useTranslation('tasks');

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-1.5" data-testid={triggerTestId}>
          <LayoutGrid className="size-4" />
          <span className="hidden md:inline">
            {t('toolbar.groupBy')}
            {value !== 'none' && (
              <span className="ml-1 text-primary">· {t(`toolbar.groupByOptions.${value}`)}</span>
            )}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {GROUP_BY_OPTIONS.map((opt) => (
          <DropdownMenuItem
            key={opt}
            onClick={() => onChange(opt)}
            data-testid={`tasks-groupby-option-${opt}`}
            className={value === opt ? 'text-primary' : undefined}
          >
            {t(`toolbar.groupByOptions.${opt}`)}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
